import React, { useState } from "react";
import { toast } from "react-toastify";
import AuthService from "./AuthService";
import axiosInstance from "./axiosInstance";
import "./Login.css";

const emptyTeacherForm = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  department: "",
  job_title: "",
  qualification: "",
  password: "",
};

const emptyParentForm = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  occupation: "",
  employer: "",
  password: "",
  child_full_name: "",
  claimed_student_number: "",
  claimed_class: "",
  relationship: "GUARDIAN",
  claim_message: "",
};

function Login() {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [activePanel, setActivePanel] = useState("login");
  const [teacherForm, setTeacherForm] = useState(emptyTeacherForm);
  const [parentForm, setParentForm] = useState(emptyParentForm);
  const [loading, setLoading] = useState(false);


  const handleLogin = async (e) => {
    e.preventDefault();

    if (!identifier || !password) {
      toast.error("Please enter your login ID and password.");
      return;
    }

    try {
      setLoading(true);
      const response = await AuthService.login(identifier, password);

      if (response.access) {
        toast.success("Login successful.");
        window.location.href = "/dashboard";
      }
    } catch (error) {
      toast.error(
        error.response?.data?.non_field_errors?.[0] ||
          error.response?.data?.detail ||
          "Invalid login details."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTeacherChange = (event) => {
    const { name, value } = event.target;
    setTeacherForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleParentChange = (event) => {
    const { name, value } = event.target;
    setParentForm((prev) => ({ ...prev, [name]: value }));
  };

  const submitTeacherRegistration = async (event) => {
    event.preventDefault();

    if (
      !teacherForm.first_name.trim() ||
      !teacherForm.last_name.trim() ||
      !teacherForm.email.trim() ||
      !teacherForm.password.trim()
    ) {
      toast.warning("First name, last name, email and password are required.");
      return;
    }

    try {
      setLoading(true);
      await axiosInstance.post("auth/register/teacher/", teacherForm);
      toast.success("Teacher account submitted. Admin approval is required.");
      setTeacherForm(emptyTeacherForm);
      setActivePanel("login");
    } catch (error) {
      const data = error.response?.data;
      const message =
        data?.detail ||
        Object.values(data || {})?.flat?.()?.[0] ||
        "Failed to submit teacher account.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const submitParentRegistration = async (event) => {
    event.preventDefault();

    const requiredParentFields = [
      parentForm.first_name,
      parentForm.last_name,
      parentForm.email,
      parentForm.password,
      parentForm.child_full_name,
      parentForm.relationship,
    ];

    if (
      requiredParentFields.some((value) => !String(value || "").trim())
    ) {
      toast.warning(
        "Parent details, password, child name and relationship are required."
      );
      return;
    }

    try {
      setLoading(true);
      await axiosInstance.post("auth/register/parent/", {
        ...emptyParentForm,
        ...parentForm,
      });
      toast.success(
        "Parent account and student claim submitted. Admin approval is required."
      );
      setParentForm(emptyParentForm);
      setActivePanel("login");
    } catch (error) {
      const data = error.response?.data;
      const message =
        data?.detail ||
        Object.values(data || {})?.flat?.()?.[0] ||
        "Failed to submit parent account.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const renderLoginPanel = () => (
    <form onSubmit={handleLogin}>
      <div className="mb-4">
        <small className="grange-card-kicker">Secure Portal Access</small>
        <h2 className="grange-login-title">Welcome back</h2>
        <p className="text-muted mb-0">
          Use your student number, employee number, email, or username.
        </p>
      </div>

      <div className="mb-3">
        <label className="form-label fw-bold small">
          Login ID
        </label>
        <input
          type="text"
          className="form-control grange-modern-input"
          placeholder="Student number, employee number, email or username"
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
        />
      </div>

      <div className="mb-3">
        <label className="form-label fw-bold small">Password</label>
        <div className="grange-password-field">
          <input
            type={showPassword ? "text" : "password"}
            className="form-control grange-modern-input"
            placeholder="Enter school password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <button type="button" onClick={() => setShowPassword(!showPassword)}>
            {showPassword ? "Hide" : "Show"}
          </button>
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <label className="grange-check">
          <input type="checkbox" />
          <span>Remember me</span>
        </label>

        <span className="small fw-bold grange-text-green">Forgot password?</span>
      </div>

      <button className="grange-login-submit" disabled={loading}>
        {loading ? "Signing in..." : "Enter Portal"}
        {!loading && <i className="bi bi-arrow-right ms-2"></i>}
      </button>

      <div className="row g-2 mt-3">
        <div className="col-6">
          <button
            type="button"
            className="btn btn-light border w-100"
            onClick={() => setActivePanel("teacher")}
          >
            Teacher signup
          </button>
        </div>
        <div className="col-6">
          <button
            type="button"
            className="btn btn-light border w-100"
            onClick={() => setActivePanel("parent")}
          >
            Parent signup
          </button>
        </div>
      </div>
    </form>
  );

  const renderTeacherPanel = () => (
    <form onSubmit={submitTeacherRegistration}>
      <div className="mb-4">
        <small className="grange-card-kicker">Teacher Self Registration</small>
        <h2 className="grange-login-title">Create teacher account</h2>
        <p className="text-muted mb-0">
          Your account will be reviewed and activated by the school admin.
        </p>
      </div>

      <div className="row g-3">
        <div className="col-md-6">
          <label className="form-label fw-bold small">First Name</label>
          <input name="first_name" className="form-control grange-modern-input" value={teacherForm.first_name} onChange={handleTeacherChange} />
        </div>
        <div className="col-md-6">
          <label className="form-label fw-bold small">Last Name</label>
          <input name="last_name" className="form-control grange-modern-input" value={teacherForm.last_name} onChange={handleTeacherChange} />
        </div>
        <div className="col-md-6">
          <label className="form-label fw-bold small">Email</label>
          <input type="email" name="email" className="form-control grange-modern-input" value={teacherForm.email} onChange={handleTeacherChange} />
        </div>
        <div className="col-md-6">
          <label className="form-label fw-bold small">Phone</label>
          <input name="phone" className="form-control grange-modern-input" value={teacherForm.phone} onChange={handleTeacherChange} />
        </div>
        <div className="col-md-6">
          <label className="form-label fw-bold small">Department</label>
          <input name="department" className="form-control grange-modern-input" value={teacherForm.department} onChange={handleTeacherChange} />
        </div>
        <div className="col-md-6">
          <label className="form-label fw-bold small">Job Title</label>
          <input name="job_title" className="form-control grange-modern-input" value={teacherForm.job_title} onChange={handleTeacherChange} />
        </div>
        <div className="col-12">
          <label className="form-label fw-bold small">Qualification</label>
          <input name="qualification" className="form-control grange-modern-input" value={teacherForm.qualification} onChange={handleTeacherChange} />
        </div>
        <div className="col-12">
          <label className="form-label fw-bold small">Password</label>
          <input type="password" name="password" className="form-control grange-modern-input" value={teacherForm.password} onChange={handleTeacherChange} />
        </div>
      </div>

      <button className="grange-login-submit mt-4" disabled={loading}>
        {loading ? "Submitting..." : "Submit Teacher Account"}
      </button>
      <button type="button" className="btn btn-link w-100 mt-2 grange-text-green" onClick={() => setActivePanel("login")}>
        Back to login
      </button>
    </form>
  );

  const renderParentPanel = () => (
    <form onSubmit={submitParentRegistration}>
      <div className="mb-4">
        <small className="grange-card-kicker">Parent Self Registration</small>
        <h2 className="grange-login-title">Create parent account</h2>
        <p className="text-muted mb-0">
          Submit your account and child claim for school verification.
        </p>
      </div>

      <div className="row g-3">
        <div className="col-12">
          <div className="fw-bold text-muted small text-uppercase">
            Parent Details
          </div>
        </div>

        <div className="col-md-6">
          <label className="form-label fw-bold small">First Name</label>
          <input name="first_name" className="form-control grange-modern-input" value={parentForm.first_name} onChange={handleParentChange} />
        </div>
        <div className="col-md-6">
          <label className="form-label fw-bold small">Last Name</label>
          <input name="last_name" className="form-control grange-modern-input" value={parentForm.last_name} onChange={handleParentChange} />
        </div>
        <div className="col-md-6">
          <label className="form-label fw-bold small">Email</label>
          <input type="email" name="email" className="form-control grange-modern-input" value={parentForm.email} onChange={handleParentChange} />
        </div>
        <div className="col-md-6">
          <label className="form-label fw-bold small">Phone</label>
          <input name="phone" className="form-control grange-modern-input" value={parentForm.phone} onChange={handleParentChange} />
        </div>
        <div className="col-md-6">
          <label className="form-label fw-bold small">Occupation</label>
          <input name="occupation" className="form-control grange-modern-input" value={parentForm.occupation} onChange={handleParentChange} />
        </div>
        <div className="col-md-6">
          <label className="form-label fw-bold small">Employer</label>
          <input name="employer" className="form-control grange-modern-input" value={parentForm.employer} onChange={handleParentChange} />
        </div>
        <div className="col-12">
          <label className="form-label fw-bold small">Password</label>
          <input type="password" name="password" className="form-control grange-modern-input" value={parentForm.password} onChange={handleParentChange} />
        </div>

        <div className="col-12 pt-2">
          <div className="fw-bold text-muted small text-uppercase">
            Student Claim
          </div>
          <small className="text-muted">
            Admin will verify these details before linking the child to your account.
          </small>
        </div>

        <div className="col-md-6">
          <label className="form-label fw-bold small">Child Full Name</label>
          <input name="child_full_name" className="form-control grange-modern-input" value={parentForm.child_full_name} onChange={handleParentChange} />
        </div>
        <div className="col-md-6">
          <label className="form-label fw-bold small">Student Number If Known</label>
          <input name="claimed_student_number" className="form-control grange-modern-input" placeholder="e.g. TGCS0000001BT" value={parentForm.claimed_student_number} onChange={handleParentChange} />
        </div>
        <div className="col-md-6">
          <label className="form-label fw-bold small">Class If Known</label>
          <input name="claimed_class" className="form-control grange-modern-input" placeholder="e.g. Form 1 Blue" value={parentForm.claimed_class} onChange={handleParentChange} />
        </div>
        <div className="col-md-6">
          <label className="form-label fw-bold small">Relationship</label>
          <select name="relationship" className="form-select grange-modern-input" value={parentForm.relationship} onChange={handleParentChange}>
            <option value="FATHER">Father</option>
            <option value="MOTHER">Mother</option>
            <option value="GUARDIAN">Guardian</option>
            <option value="SPONSOR">Sponsor</option>
            <option value="OTHER">Other</option>
          </select>
        </div>
        <div className="col-12">
          <label className="form-label fw-bold small">Message To Admin</label>
          <textarea name="claim_message" rows="3" className="form-control grange-modern-input" placeholder="Optional note that helps the school verify your relationship." value={parentForm.claim_message} onChange={handleParentChange}></textarea>
        </div>
      </div>

      <button className="grange-login-submit mt-4" disabled={loading}>
        {loading ? "Submitting..." : "Submit Parent Account"}
      </button>
      <button type="button" className="btn btn-link w-100 mt-2 grange-text-green" onClick={() => setActivePanel("login")}>
        Back to login
      </button>
    </form>
  );

  return (
    <div className="grange-login-panel-scroll">
      {activePanel === "teacher" && renderTeacherPanel()}
      {activePanel === "parent" && renderParentPanel()}
      {activePanel === "login" && renderLoginPanel()}
    </div>
  );
}

export default Login;
